import React from 'react';

import { cn } from '../../lib/utils';
import type { AttachmentBlock, ContextPill, FileAttachmentBlock, Message, TextBlock } from './types';
import { ContextPillBadge } from './ContextPillBadge';
import { MessageImageThumbnails } from './AttachmentPreviews';
import { mimeTypeToIconType } from './attachmentUtils';

interface ConversationMessageUserProps {
  message: Message;
  className?: string;
}

function attachmentBlockKey(block: AttachmentBlock): string {
  switch (block.type) {
    case 'file':
      return `file:${block.attachmentId}`;
    case 'page':
      return `page:${block.url}`;
    case 'entity':
      return `entity:${block.entity.type}:${block.entity.id}`;
  }
}

function attachmentBlockToPill(block: AttachmentBlock): ContextPill {
  switch (block.type) {
    case 'file':
      return {
        id: block.attachmentId,
        label: block.fileName,
        iconType: mimeTypeToIconType(block.mimeType),
      };
    case 'page':
      return { id: block.url, label: block.title, iconType: 'page' };
    case 'entity':
      return { id: block.entity.id, label: block.entity.title, iconType: block.entity.type };
  }
}

export const ConversationMessageUser = ({ message, className }: ConversationMessageUserProps) => {
  const textBlocks = message.content.filter((b): b is TextBlock => b.type === 'text');
  const imageBlocks = message.content.filter(
    (b): b is FileAttachmentBlock => b.type === 'file' && b.mimeType.startsWith('image/'),
  );
  const otherAttachmentBlocks = message.content.filter((b): b is AttachmentBlock => {
    if (b.type === 'text') return false;
    if (b.type === 'file' && b.mimeType.startsWith('image/')) return false;

    return true;
  });

  const paragraphs = textBlocks
    .map((b) => b.text)
    .join('\n')
    .split(/\n\n+/)
    .filter((p) => p.trim().length > 0);

  return (
    <div className={cn('flex justify-end', className)}>
      <div className="max-w-[85%] rounded bg-dark-200 px-4 py-2.5 text-foreground flex flex-col gap-3">
        <MessageImageThumbnails blocks={imageBlocks} />
        {otherAttachmentBlocks.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {otherAttachmentBlocks.map((block) => (
              <ContextPillBadge key={attachmentBlockKey(block)} pill={attachmentBlockToPill(block)} />
            ))}
          </div>
        )}
        {paragraphs.length > 0 && (
          <div>
            {paragraphs.map((paragraph, i) => (
              <p key={i} className="m-0 mb-4 whitespace-pre-wrap last:mb-0">
                {paragraph}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
ConversationMessageUser.displayName = 'ConversationMessageUser';